import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faPrint } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { ButtonIcon } from "../Button/Button"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import { useSavePrint, type PrintTargetOption } from "../../hooks/Order/useSavePrint"
import { type IFiscalDocument } from "../../Objects/FiscalIntegration/IFiscalDocument"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"

type Props = {
  document: IFiscalDocument | null
  onClose: () => void
  onConfirm: (documentId: number, printerConfigurationId: number) => Promise<void>
}

export const FiscalDocumentPrintPopup: React.FC<Props> = ({ document, onClose, onConfirm }) => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const { getActivePrintTargets } = useSavePrint()

  const [options, setOptions] = useState<PrintTargetOption[]>([])

  const [selectedValue, setSelectedValue] = useState("")

  const [isLoading, setIsLoading] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let isActive = true

    if (!document) {
      setOptions([])
      
      setSelectedValue("")
      
      setIsLoading(false)

      return () => {
        isActive = false
      }
    }

    setOptions([])
    
    setSelectedValue("")
    
    setIsLoading(true)

    void getActivePrintTargets("delivery")
      .then((targets) => {
        if (!isActive) {
          return
        }

        const printerTargets = targets.filter((target) => target.printerConfigurationId != null)
        setOptions(printerTargets)
        setSelectedValue(printerTargets[0]?.value ?? "")
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [document])

  const selectedPrinterId = useMemo(
    () => options.find((option) => option.value === selectedValue)?.printerConfigurationId,
    [options, selectedValue],
  )

  const handleConfirm = async () => {
    if (!document || !selectedPrinterId || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      await onConfirm(document.id, selectedPrinterId)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!document) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              icon={faPrint}
              iconColor={THEME.colors.iconBGColor}
              title={t("documents.printTitle")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.print.1"
                  title={t("helpPopup:global.fiscalPrint.title")}
                  description={t("helpPopup:global.fiscalPrint.description")}
                  steps={t("helpPopup:global.fiscalPrint.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalPrint.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentPrintPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="space-y-4 p-4">
              <p className="text-sm leading-relaxed text-slate-600">
                {t("documents.printDescription", { number: document.number ?? document.reference })}
              </p>

              {options.length > 0 ? (
                <GenericDropDown
                  name="fiscal-print-target"
                  data={options.map((option) => ({ label: option.label, value: option.value }))}
                  initialState={selectedValue}
                  handlerFuncion={(event) => setSelectedValue(event.currentTarget.value)}
                  disable={isLoading || isSubmitting}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentPrintPopup.select.printer"
                />
              ) : (
                <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {isLoading ? t("documents.loadingPrinters") : t("documents.noPrinters")}
                </p>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={isLoading || isSubmitting || !selectedPrinterId}
                  onClick={() => void handleConfirm()}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentPrintPopup.button.confirm"
                >
                  <FontAwesomeIcon icon={faPrint} />

                  {isSubmitting ? t("documents.printing") : t("documents.confirmPrint")}
                </button>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
